import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import QuizDetailsLanding from '@cont/Quiz/Details/Landing';
import QuizTakingLanding from '@cont/Quiz/Taking/Landing/Landing';
import RevampQuizTakingLanding from '@cont/Quiz/RevampedTaking/Landing/Landing';
import NtaTakingLanding from '@cont/Quiz/NtaExperience/Taking/NtaTakingLanding';
import { getCurrentSessionUID, getQuizDetails } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import useConstants from '@api/hooks/useConstants';
import { getIsTestRevampGoal } from '@api/selectors/constants';
import {
  ProctorProvider,
  useProctorViolationTermination,
  ViolationTerminatedDialog
} from '@cont/Quiz/Proctoring';
import { getIsQuizProctored } from '@cont/Quiz/utils';
import { getIsCbtProgramme } from './Details/utils';
import QuizStatusProvider from './Details/Common/QuizStatusProvider';
import BaseLayout from './Common/BaseLayout';
import InstructionsPage from './Details/TestRevamp/InstructionsPage';
import NtaInstructionsPage from './NtaExperience/Instructions/InstructionsPage';

// Rendered as ProctorProvider's child so the violation watcher can read the
// proctoring context, and mounted around {content} (not just the taking
// view) so the dialog survives the switch to QuizDetailsLanding once the
// auto-submit clears the session.
const ProctoredLayoutContent = ({ children }) => {
  const { showViolationDialog, closeViolationDialog } =
    useProctorViolationTermination();

  return (
    <>
      {children}
      <ViolationTerminatedDialog
        show={showViolationDialog}
        onClose={closeViolationDialog}
      />
    </>
  );
};

ProctoredLayoutContent.propTypes = {
  children: PropTypes.node.isRequired
};

const Layout = ({ children }) => {
  const { query } = useRouter();
  const { show_instructions, quizUID } = query;

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const currentSessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const isCbtProgramme = getIsCbtProgramme(quizDetails);
  const isTestRevamp = useConstants(getIsTestRevampGoal(quizDetails.goal_uid));
  const { isLive, isProctored } = getIsQuizProctored(quizDetails);

  // Wrap entire quiz flow with single ProctorProvider
  // This ensures SDK persists across instructions drawer → taking page transition
  const content = (() => {
    if (show_instructions) {
      if (isCbtProgramme) {
        return (
          <QuizStatusProvider>
            <NtaInstructionsPage />
          </QuizStatusProvider>
        );
      }

      return <InstructionsPage />;
    }

    if (currentSessionUID) {
      if (isCbtProgramme) {
        return (
          <>
            {children}
            <NtaTakingLanding />
          </>
        );
      }
      return (
        <>
          {children}
          {isTestRevamp ? <RevampQuizTakingLanding /> : <QuizTakingLanding />}
        </>
      );
    }

    return (
      <>
        {children}
        <BaseLayout>
          <QuizDetailsLanding />
        </BaseLayout>
      </>
    );
  })();

  // Only wrap with ProctorProvider if quiz is proctored
  if (isProctored) {
    return (
      <ProctorProvider
        quizUID={quizUID}
        quizTitle={quizDetails?.title}
        quizDuration={
          quizDetails?.duration_seconds ||
          quizDetails?.time_limit ||
          quizDetails?.duration
        }
        quizStartTime={
          quizDetails?.scheduled_at ||
          quizDetails?.start_time ||
          new Date().toISOString()
        }
        enabled
        allowSessionRestore
        isLive={isLive}
      >
        <ProctoredLayoutContent>{content}</ProctoredLayoutContent>
      </ProctorProvider>
    );
  }

  return content;
};

Layout.propTypes = {
  children: PropTypes.node.isRequired
};

export default Layout;
